import { merge } from 'remeda';
import React, { FunctionComponent, Fragment } from 'react';
import { Loader } from 'semantic-ui-react';
import {
  ExportGuideProViewsTableQuery,
  ProView,
  useExportGuideProViewsTableQuery,
} from 'generated/graphql';
import SortableTable from '../sortable-table/sortable-table';

type ManageProsContainerProps = {
  proViewItems: ProViewItem[];
  bilikZoneId?: number;
  onItemChange: (items: ProViewItem[]) => void;
};

type ProViewItem = ProView & {
  isChecked: boolean;
  hasGuidePhoneNumber: boolean;
  trades: {
    name: string;
    isTradeGuide: boolean;
  }[];
};

export type ManageProViewContainerItem = ProViewItem;

const ManageProsContainer: FunctionComponent<ManageProsContainerProps> = ({
  onItemChange,
  bilikZoneId,
  proViewItems,
}) => {
  const { error, data, loading } = useExportGuideProViewsTableQuery({
    variables: {
      where: {
        status: {
          _in: ['draft', 'published'],
        },
        bilikZone: {
          id: {
            _eq: Number(bilikZoneId),
          },
          status: {
            _eq: 'published',
          },
        },
      },
    },
  });

  if (error) return <Fragment>Error! ${error.message}</Fragment>;

  if (loading) {
    return (
      <Loader size="large" active inline="centered">
        Chargement...
      </Loader>
    );
  }

  let shuffledProViews: ExportGuideProViewsTableQuery['proView'] = [];

  if (data) {
    shuffledProViews = data.proView.slice().sort(() => Math.random() - 0.5);
  }

  const items: ProViewItem[] = shuffledProViews
    .map((proView) => {
      const tradeGuides = proView.proPresentation.matchingTradeGuides?.map(
        (tradeGuide) => ({
          name: tradeGuide.name,
          isTradeGuide: true,
        }),
      );

      const mainTrades = proView.proPresentation.mainTrades?.map(
        ({ trade }) => ({
          name: trade.name,
          isTradeGuide: false,
        }),
      );

      const trades = tradeGuides?.length ? tradeGuides : mainTrades;

      return merge(proView, {
        isChecked: false,
        hasGuidePhoneNumber: (proView.phoneNumbers ?? []).length > 0,
        trades,
      }) as ProViewItem;
    })

    // Sort on normalized main trade name
    .sort((a, b) => a.trades?.[0]?.name.localeCompare(b.trades?.[0].name));

  return proViewItems.length > 1 ? (
    <SortableTable onItemChange={onItemChange} items={proViewItems} />
  ) : (
    <SortableTable onItemChange={onItemChange} items={items} />
  );
};

export default ManageProsContainer;
